/**
 * Keyboard shortcuts for Smart Review session — UI Wave A.
 * Plus a `?`-triggered cheatsheet modal.
 *
 * Bindings:
 *   1 / A → pick choice A
 *   2 / B → pick choice B
 *   3 / C → pick choice C
 *   4 / D → pick choice D
 *   Space, Enter → submit current selection / advance to next
 *   R → reveal rationale toggle
 *   N → next question (when result shown)
 *   ? → open cheatsheet
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

const SHORTCUTS = [
  { keys: ["1", "A"], label: "Pick choice A" },
  { keys: ["2", "B"], label: "Pick choice B" },
  { keys: ["3", "C"], label: "Pick choice C" },
  { keys: ["4", "D"], label: "Pick choice D" },
  { keys: ["Space", "Enter"], label: "Submit / Next" },
  { keys: ["R"], label: "Toggle rationale" },
  { keys: ["?"], label: "Show this cheatsheet" },
];

export function useReviewKeyboardShortcuts({
  enabled,
  letters,         // e.g. ['A','B','C','D'] - the original letters in current order
  onPick,          // (letter) => void
  onSubmit,        // () => void
  onNext,          // () => void
  hasResult,       // boolean — when true, Space/Enter calls onNext instead of onSubmit
  onToggleRationale,
}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      // Ignore when focused inside inputs / textareas / contenteditable
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      // Choice picking
      if (letters && (k === "1" || k === "2" || k === "3" || k === "4")) {
        const idx = parseInt(k, 10) - 1;
        if (letters[idx]) { onPick?.(letters[idx]); e.preventDefault(); return; }
      }
      const upper = k.toUpperCase();
      if (letters && (upper === "A" || upper === "B" || upper === "C" || upper === "D")) {
        if (letters.includes(upper)) { onPick?.(upper); e.preventDefault(); return; }
      }
      if (k === " " || k === "Enter") {
        if (hasResult) onNext?.(); else onSubmit?.();
        e.preventDefault();
        return;
      }
      if (upper === "R") { onToggleRationale?.(); e.preventDefault(); }
      if (upper === "N" && hasResult) { onNext?.(); e.preventDefault(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, letters, onPick, onSubmit, onNext, hasResult, onToggleRationale]);
}

export function ShortcutCheatsheet() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        setOpen(true);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="shortcut-cheatsheet">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-base flex items-center gap-2">
            <Keyboard className="w-4 h-4" /> Keyboard shortcuts
          </DialogTitle>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm">
          {SHORTCUTS.map((s) => (
            <li key={s.label} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
              <span className="text-slate-700">{s.label}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k, i) => (
                  <kbd
                    key={k}
                    className="px-1.5 py-0.5 text-[11px] font-bold rounded-md bg-slate-100 border border-slate-300 text-slate-700 tabular-nums"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-slate-400 text-center mt-2">
          Press <kbd className="px-1 bg-slate-100 rounded border">?</kbd> anytime to show this.
        </p>
      </DialogContent>
    </Dialog>
  );
}
